/**
 * Two-phase operator form (EasyAdmin pattern).
 *
 * Many admin mutations are dangerous (adjust-balance, freeze, reset-password).
 * We match the backend `xian` flag protocol:
 *
 *   Phase 1 — xian=true  → server validates + returns a "preview" payload
 *                          (e.g. balance_before, balance_after). We show it
 *                          inside a confirm modal, giving the operator a last
 *                          chance to bail.
 *   Phase 2 — xian=false → after the operator clicks "Confirm", we re-submit
 *                          with the same body, and the server actually writes.
 *
 * The component takes care of wiring the Ant Form + the modal round-trip.
 *
 * Usage:
 *   <OperatorForm
 *     endpoint={`/admin/users/${user.id}/adjust-balance`}
 *     fields={[
 *       { name: 'asset',   label: 'Asset',  type: 'select', options: tokens },
 *       { name: 'delta',   label: 'Delta',  type: 'text',  required: true },
 *       { name: 'remark',  label: 'Remark', type: 'textarea' },
 *     ]}
 *     renderPreview={(data) => <Descriptions>...</Descriptions>}
 *     onDone={() => qc.invalidateQueries(['users'])}
 *   />
 */
import { Button, Form, Input, InputNumber, Modal, Select, message } from 'antd';
import { useState } from 'react';
import { api } from '../api/client';
import { ApiError } from '../api/client';

type FieldType = 'text' | 'number' | 'textarea' | 'select';

export interface OperatorFormField {
  name: string;
  label: string;
  type: FieldType;
  required?: boolean;
  options?: { value: string | number; label: string }[];
  placeholder?: string;
  initialValue?: unknown;
}

interface OperatorFormProps<P = Record<string, unknown>> {
  endpoint: string;
  fields: OperatorFormField[];
  submitLabel?: string;
  modalWidth?: number;
  /** Axios timeout ms (placement-move / large trees). */
  timeoutMs?: number;
  renderPreview?: (preview: P) => React.ReactNode;
  onDone?: () => void;
}

export function OperatorForm<P = Record<string, unknown>>(props: OperatorFormProps<P>) {
  const {
    endpoint,
    fields,
    submitLabel = '预览并校验',
    modalWidth = 520,
    timeoutMs,
    renderPreview,
    onDone,
  } = props;
  const [form] = Form.useForm();
  const [preview, setPreview] = useState<P | null>(null);
  const [pendingValues, setPendingValues] = useState<Record<string, unknown> | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);

  async function handleSubmit(values: Record<string, unknown>) {
    setSubmitting(true);
    try {
      const data = await api.post<P>(
        endpoint,
        { ...values, xian: true },
        timeoutMs != null ? { timeout: timeoutMs } : undefined,
      );
      setPreview(data);
      setPendingValues(values);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : '预览失败';
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleConfirm() {
    if (!pendingValues) return;
    setConfirmLoading(true);
    try {
      await api.post(
        endpoint,
        { ...pendingValues, xian: false },
        timeoutMs != null ? { timeout: timeoutMs } : undefined,
      );
      message.success('操作已完成');
      setPreview(null);
      setPendingValues(null);
      form.resetFields();
      onDone?.();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : '操作失败';
      message.error(msg);
    } finally {
      setConfirmLoading(false);
    }
  }

  return (
    <>
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        {fields.map((f) => (
          <Form.Item
            key={f.name}
            name={f.name}
            label={f.label}
            initialValue={f.initialValue}
            rules={f.required ? [{ required: true, message: `请填写${f.label}` }] : []}
          >
            {f.type === 'text' && <Input placeholder={f.placeholder} />}
            {f.type === 'number' && (
              <InputNumber style={{ width: '100%' }} placeholder={f.placeholder} />
            )}
            {f.type === 'textarea' && <Input.TextArea rows={3} placeholder={f.placeholder} />}
            {f.type === 'select' && <Select options={f.options} placeholder={f.placeholder} />}
          </Form.Item>
        ))}
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={submitting}>
            {submitLabel}
          </Button>
        </Form.Item>
      </Form>

      <Modal
        open={preview !== null}
        title="确认执行"
        width={modalWidth}
        onOk={handleConfirm}
        onCancel={() => {
          setPreview(null);
          setPendingValues(null);
        }}
        confirmLoading={confirmLoading}
        okText="确认执行"
        cancelText="取消"
      >
        {preview && renderPreview
          ? renderPreview(preview)
          : <pre style={{ margin: 0 }}>{JSON.stringify(preview, null, 2)}</pre>}
      </Modal>
    </>
  );
}
